import { api } from "@/api";
import { useApiMutate } from "@/api/hooks/useApiMutate";
import type { CreateFeedbackDto } from "@/api/types";
import { FeedbackSentiment } from "@/api/types";
import { H3 } from "@/components/Heading";
import { FeedbackSentimentSelect } from "@/pages/Feedback/FeedbackSentimentSelect";
import { Button, Stack, Text, Textarea } from "@chakra-ui/react";
import React, { useState } from "react";
import toast from "react-hot-toast";
import { telemetry } from "@/telemetry";
import type { RequestResult } from "@/telemetry/events";
import { feedbackSentiments } from "@/telemetry/events";

interface State {
  comment: string;
  email?: string;
  orderId: number;
  sentiment?: FeedbackSentiment;
}

interface Props {
  orderId: number;
}

const trackFeedbackSubmitted = (
  feedback: CreateFeedbackDto,
  result: RequestResult,
) =>
  telemetry.trackEvent("feedback_submitted", {
    sentiment: feedbackSentiments[feedback.sentiment],
    has_comment:
      feedback.sentiment === FeedbackSentiment.ThumbsDown &&
      !!feedback.comment.trim(),
    source: "confirmation",
    result,
  });

export const OrderFeedbackPanel = ({ orderId }: Props) => {
  const [state, setState] = useState<State>({
    comment: "",
    email: "",
    orderId,
  });

  const [wasSubmitted, setWasSubmitted] = useState(false);

  const isNegative = state.sentiment === FeedbackSentiment.ThumbsDown;

  const apiSubmit = useApiMutate({
    mutationFn: api.createFeedback,
    onSuccess: (_, feedback) => {
      trackFeedbackSubmitted(feedback, "ok");
      setWasSubmitted(true);
    },
    onError: (_, feedback) => {
      trackFeedbackSubmitted(feedback, "error");
      toast.error(
        "There was an error submitting your feedback. Please try again.",
      );
    },
  });

  const handleSentimentSelect = (sentiment: FeedbackSentiment) => {
    if (wasSubmitted) return;

    telemetry.trackEvent("feedback_sentiment_selected", {
      sentiment: feedbackSentiments[sentiment],
      source: "confirmation",
    });

    setState((prev) => ({ ...prev, sentiment }));

    if (sentiment === FeedbackSentiment.ThumbsUp)
      apiSubmit.mutate({
        comment: "Positive",
        orderId: state.orderId,
        sentiment,
      });
  };

  const handleSubmit = () =>
    apiSubmit.mutate({
      comment: state.comment,
      email: state.email,
      orderId: state.orderId,
      sentiment: state.sentiment!,
    });

  return (
    <Stack
      p="12"
      borderRadius="12px"
      bg="primary"
      alignItems="center"
      spacing="8"
    >
      <H3 color="white">How was your ordering experience?</H3>

      <FeedbackSentimentSelect
        value={state.sentiment}
        onSelect={handleSentimentSelect}
        isDisabled={wasSubmitted}
      />

      {isNegative && (
        <Stack
          spacing="3"
          justifyContent="center"
        >
          <Stack
            spacing="3"
            opacity={wasSubmitted ? 0.5 : 1}
            pointerEvents={wasSubmitted ? "none" : "auto"}
          >
            <Text
              color="white"
              textAlign="center"
            >
              Please let us know how we can improve.
            </Text>
            <Textarea
              rows={4}
              autoFocus
              minWidth="500px"
              maxWidth="100%"
              value={state.comment}
              placeholder="Type feedback here (required)"
              onChange={(e) =>
                setState((prev) => ({ ...prev, comment: e.target.value }))
              }
            />
          </Stack>
          {!wasSubmitted && (
            <Button
              onClick={handleSubmit}
              isLoading={apiSubmit.isPending}
              bg="black"
              _hover={{ bg: "black" }}
              m="auto"
            >
              Submit
            </Button>
          )}
        </Stack>
      )}

      {wasSubmitted && <H3>Thank you for your feedback.</H3>}
    </Stack>
  );
};
